import Script from "next/script"
import { type ReactNode, useEffect, useRef } from "react"
import { ContentBox } from "@/components/resources/components/ContentBox"
import { EmbedVideo } from "@/components/resources/components/ui/EmbedVideo"

declare global {
  interface Window {
    twttr?: { widgets: { load: (element?: HTMLElement | null) => void } }
  }
}

/** `<EmbedTweet url="https://twitter.com/..." />`. widgets.js turns the link into the tweet. */
const MarkdownEmbedTweet = ({ url }: { url: string }) => {
  const ref = useRef<HTMLDivElement>(null)

  // widgets.js only scans the page when it loads, so tweets added by client-side navigation need a nudge
  useEffect(() => {
    window.twttr?.widgets.load(ref.current)
  }, [])

  return (
    <div ref={ref} className="mb-4 md:mb-6">
      <Script src="https://platform.twitter.com/widgets.js" />
      <blockquote className="twitter-tweet" data-theme="dark">
        <a href={url}>{url}</a>
      </blockquote>
    </div>
  )
}

/** `<EmbedVideo url="https://www.youtube.com/watch?v=..." />` */
const MarkdownEmbedVideo = ({ url }: { url: string }) => (
  <EmbedVideo url={url} className="mb-4 md:mb-6 pt-xs md:pt-s" />
)

/** `<ContentBox title="Optional title">Markdown content</ContentBox>` */
const MarkdownContentBox = ({ title, children }: { title?: string; children: ReactNode }) => (
  <>
    {title && <p className="leading-none font-medium mb-2 md:mb-4 text-fg-secondary">{title}</p>}
    <ContentBox className="mb-4">{children}</ContentBox>
  </>
)

// MDX components for the Sanity block types that posts use. Sanity tables are GFM tables, and content breaks are `---`.
export const markdownEmbedComponents = {
  EmbedTweet: MarkdownEmbedTweet,
  EmbedVideo: MarkdownEmbedVideo,
  ContentBox: MarkdownContentBox,
}
